import * as React from "react";
import { Button, Col, Empty, Row, Space, Table } from "antd";
import { colors, formatter, sharesColumns } from "./table-column";
import { connect } from "react-redux";
import { TReducers } from "@features/reducers";
import Big from "big.js";
import { IPESWalletDetails } from "@market/interfaces/IWallet";
import Chart from "./parts/Chart/Chart";
import { useMediaQuery } from "react-responsive";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import { REQUEST_TO_SELL_SHARES_URL } from "../../config";

import * as style from "./MySharesPage.module.scss"
const big = Big();

type Props = {
  userPublicShares: IPESWalletDetails[];
} & RouteComponentProps;

const MySharesPage = ({ userPublicShares = [], match }: Props) => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const chartRef = React.useRef<HTMLDivElement>(null);
  const [legendHeight, setLegendHeight] = React.useState(0);

  const walletsShares = userPublicShares.filter(
    (wallet) => !Big(wallet.balance ?? 0).eq(0)
  );

  const data = walletsShares?.map((balance) => ({
    total_usd: isNaN(balance.total_usd) ? 0 : Number(balance.total_usd),
    currency_name: balance.currency_name,
    currency: balance.currency,
  }));

  const additionalPadding = React.useMemo(() => {
    return legendHeight > 0 ? legendHeight + 24 : 0;
  }, [legendHeight]);

  React.useEffect(() => {
    const calculateLegendHeight = () => {
      if (chartRef.current) {
        const legend = chartRef.current.querySelector(".apexcharts-legend");
        const height = legend ? legend.clientHeight : 0;
        setLegendHeight(height);
      }
    };

    const timer = setTimeout(calculateLegendHeight, 300);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Row>
      <Col span={17} xs={24} sm={24} lg={17}>
        <Table
          rowKey={"id"}
          locale={{
            emptyText: <Empty description="No Public Shares Yet" />,
          }}
          columns={sharesColumns({
            baseUrl: match.url.replace("/public-equity", ""),
            isMobile,
          })}
          className={style.table}
          dataSource={walletsShares}
          expandable={{
            showExpandColumn: false,
            defaultExpandAllRows: false,
            expandRowByClick: isMobile,
            fixed: true,
            expandedRowRender: (record) => (
              <div className={style.expandedRowContent}>
                <div className={style.expandedRowSection}>
                  <p className={style.expandedRowSectionTitle}>Share Price: </p>
                  <p>
                    {typeof record.share_price === "string"
                      ? record.share_price
                      : formatter.format(record.share_price ?? 0)}
                  </p>
                </div>

                <div className={style.expandedRowSection}>
                  <p className={style.expandedRowSectionTitle}>Valuation:</p>
                  <p>
                    {typeof record.total_usd === "string" &&
                    isNaN(parseFloat(record.total_usd))
                      ? record.total_usd
                      : formatter.format(
                          parseFloat(record.total_usd.toString() ?? "0")
                        )}
                  </p>
                </div>
                <Space wrap className={style.actions}>
                  <Link
                    to={`${match.url.replace(
                      "/public-equity",
                      ""
                    )}${REQUEST_TO_SELL_SHARES_URL}?share=${record.currency}`}
                  >
                    <Button>Request to sell</Button>
                  </Link>
                </Space>
              </div>
            ),
          }}
          loading={userPublicShares === undefined}
          pagination={false}
          scroll={{ y: 620 }}
        />
      </Col>
      <Col span={7} xs={24} sm={24} lg={7} ref={chartRef}>
        {userPublicShares?.length !== 0 && (
          <div
            className={style.chart}
            style={{ paddingBottom: additionalPadding }}
          >
            <p className={style.header}>Funds value</p>
            <Chart
              data={data}
              sum={walletsShares.reduce(
                (prev: number, curr: IPESWalletDetails) => {
                  if (isNaN(curr.total_usd)) {
                    return prev;
                  }

                  return Number(big(prev).add(curr.total_usd || 0));
                },
                0
              )}
              colors={colors}
            />
          </div>
        )}
      </Col>
    </Row>
  );
};

const mapStateToProps = (store: TReducers) => ({
  userPublicShares: store.fundsReducer.pesUserWallets,
});

export default withRouter(connect(mapStateToProps)(MySharesPage));
